import { trpc } from '@tencent/tpro_rmonitor_user_manager_user_role_auth';
import { Alert, AlertProps } from 'antd';
import classnames from 'classnames';
import moment from 'moment';
import React, { useEffect, useState, FC } from 'react';
import { useLocation } from 'react-router-dom';
import { getUserId } from '@/infrastructure/auth';
import { getAxios } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { getProductId } from '@/infrastructure/product-info';
import { storageGet, storageSet } from '@/infrastructure/storage-utils';
import { EXPIRE_OPTIONS, getAllRoles, getAllUsers, getRoleByUser, UserItem } from '@/presentation/components/user-manage/api';
import { ApplyMode, CreateApply } from '@/presentation/components/user-manage/create-apply';
import { useStores } from '@/presentation/store';
import { IGetSDKVersionUpgradeTipRsp, overviewService } from '@/service/overview';
import { WithTypeCast } from '@raft/utils';
import { RolesOption, User } from '../user-manage/types';
import style from './index.module.scss';
type IRole = WithTypeCast<trpc.rmonitor.user_manager.IRole>;

export const getLocalBanner = () => {
  try {
    return JSON.parse(storageGet('banner') ?? '');
  } catch (e) {
    return null;
  }
};

interface BannerData {
  priority?: number;// 数字越小，优先显示
  content?: string;
  id?: string;
  record?: IGetSDKVersionUpgradeTipRsp & {
    role?: string | null;
  };
  alertProps?: AlertProps;
}

interface IProps {
  showBanner: boolean,
  setShowBanner: React.Dispatch<React.SetStateAction<boolean>>;
  liteModeRoutes: string[];
}

export const Banner: FC<IProps> = (props) => {
  const { showBanner, setShowBanner, liteModeRoutes } = props;
  const localBanner = getLocalBanner();
  const [bannerData, setBannerData] = useState<BannerData>({});
  const [totalMemberList, setTotalMemberList] = useState<User[]>([]);
  const [rolesOption, setRolesOption] = useState<RolesOption[]>([]);
  const { loaded, rawPerm } = useStores().ProductStore;
  const location = useLocation();
  const expireTime = moment(rawPerm.expireTime, 'X');
  const [adminList, setAdminList] = useState<{ label: string; value: string }[]>();
  const [isAdmin, setIsAdmin] = useState(false);

  const getTotalMemberList = async () => {
    const data = await getAllUsers();
    setTotalMemberList(data);
    const isAdmin = await getRoleByUser();
    setIsAdmin(isAdmin);
  };

  useEffect(() => {
    async function getBanner() {
      getTotalMemberList();
      const allRoles = await getAllRoles();
      const rolesOption: RolesOption[] = allRoles.map((item: IRole) => ({
        value: item.id ?? 0,
        label: item.name ?? '',
      }));
      setRolesOption(rolesOption);
      // 1.权限到期提醒
      if (rawPerm.expireTime !== 0 && expireTime.diff(moment(), 'days') <= 7
        && !liteModeRoutes.includes(location.pathname)) {
        setBannerData({
          priority: 1,
          alertProps: { closable: false, showIcon: true, type: 'warning' },
          record: {
            role: allRoles.find(r => r.id === rawPerm.roleId)?.name,
          },
        });
        setShowBanner(true);
        return;
      }
      // 2
      const notice = await getAxios().get(`/${API_PREFIX}/api/notice/board-msg`);
      const isShowBanner = (localBanner?.id !== notice?.data?.id || localBanner?.isShowBanner !== false)
        && !!notice?.data?.content;
      const bannerData: BannerData = {
        content: notice?.data?.content ?? '',
        id: notice?.data?.id ?? '',
        priority: 2,
        alertProps: {
          closable: notice?.data?.closable ?? false,
          showIcon: false,
        },
      };
      if (isShowBanner) {
        setBannerData(bannerData);
        storageSet('banner', JSON.stringify({ ...bannerData, isShowBanner }));
        setShowBanner(true);
        return;
      }

      // 3.提醒升级sdk
      const productId = getProductId();

      if (!productId) {
        setShowBanner(false);
        return;
      }

      if (['/workbench/apps', '/config-manage', '/silence'].includes(location.pathname)) {
        setShowBanner(false);
        return;
      }
      const sdkTips = await overviewService.getSDKVersionUpgradeTip({
        productId: getProductId(),
      });

      if (sdkTips.upgradeRequired) {
        setBannerData({
          priority: 3,
          alertProps: { closable: true, showIcon: false, type: 'warning' },
          record: {
            ...sdkTips,
          },
        });
        setShowBanner(true);
        return;
      }
      setShowBanner(false);
    }
    loaded && getBanner();
  }, [rawPerm.expireTime, loaded, rawPerm.roleId, location.pathname]);

  useEffect(() => {
    const adminRoleId = rolesOption.filter(role => role.label === '管理员')?.[0]
      ?.value;
    const adminAll = totalMemberList
      .filter((member: UserItem) => member.roleId === adminRoleId)
      .map((admin: UserItem) => ({ label: admin.name, value: admin.name }));
    setAdminList(adminAll);
  }, [totalMemberList]);

  const message = (bannerData: BannerData) => {
    switch (bannerData.priority) {
      case 1:
        return <div style={{ textAlign: 'left' }}>
          尊敬的用户，您的{bannerData?.record?.role}权限将于{expireTime.format('YYYY年MM月DD日')}
          到期，请及时
          <span style={{ color: 'rgb(0,98,255)' }}>
        <CreateApply
          role={Number(rawPerm.roleId)}
          render={() => (
            <span style={{ color: 'rgb(0,98,255)', cursor: 'pointer' }}>
              申请续期
            </span>
          )}
          applyer={getUserId()}
          expireOptions={EXPIRE_OPTIONS}
          userInfo={totalMemberList.filter(member => member.name === getUserId())?.[0]}
          rolesOption={rolesOption}
          adminList={adminList}
          applyMode={ApplyMode.INDIVIDUAL}
          isAdmin={isAdmin}
        />
      </span>
          。
        </div>;
      case 2:
        return <div className={style.bannerMessage}>{bannerData.content}</div>;
      case 3:
        return <div className={style.bannerMessage}>
          <span>我们发现您当前使用的Bugly SDK版本较低，建议您升级到{bannerData?.record?.recommendSdkVersion}
            或更高版本以获得更好的服务，详细信息请参阅{bannerData.record?.upgradeLink ? <a
            href={bannerData.record?.upgradeLink}
            target='_blank'>《Bugly SDK 升级指引》</a> : '《Bugly SDK 升级指引》'}。</span>
        </div>;
      default:
        return '';
    }
  };

  return showBanner
    ? <Alert
      {...(bannerData?.alertProps ?? {})}
      message={message(bannerData)}
      banner
      className={classnames(style.banner)}
      onClose={() => {
        setShowBanner(false);
        storageSet(
          'banner',
          JSON.stringify({ ...getLocalBanner(), isShowBanner: false }),
        );
      }}
    />
    : null;
};
